<template>
    <b-modal
        :id="id"
        hide-footer
        title-class="sip-modal-title"
        body-class="sip-photo-drawer-body p-0"
        dialog-class="sip-photo-drawer-dialog"
        @show="onModalShow"
        @hidden="onModalHidden"
    >
        <template #modal-title>
            <div class="d-flex align-items-center">
                <div class="sip-photo-drawer-header-icon me-2">
                    <i class="fi fi-rr-camera text-primary"></i>
                </div>
                <div class="sip-photo-drawer-header-title">{{ modalTitle }}</div>
            </div>
        </template>

        <div class="sip-photo-drawer-content">
            <div class="sip-photo-drawer-scroll-body">
                <!-- Drag & Drop Upload Zone (when no file staged) -->
                <div
                    v-if="!selectedFile"
                    class="sip-photo-dropzone-box"
                    :class="{ 'is-dragover': isDragOver }"
                    @dragenter.prevent="onDragEnter"
                    @dragover.prevent="onDragEnter"
                    @dragleave.prevent="onDragLeave"
                    @drop.prevent="onDrop"
                    @click="triggerFileInput"
                >
                    <input
                        type="file"
                        ref="fileInput"
                        class="sip-photo-hidden-input"
                        accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                        @change="onFileChange"
                    />

                    <div class="sip-photo-dropzone-icon-circle">
                        <i class="fi fi-rr-cloud-upload"></i>
                    </div>

                    <div class="sip-photo-dropzone-text">
                        <span class="sip-photo-dropzone-primary-text">
                            {{ trans('student.drop_photo_here') || 'Drag & drop or' }}
                            <span class="sip-photo-browse-link">{{ trans('general.browse') || 'Browse' }}</span>
                        </span>
                        <span class="sip-photo-dropzone-subtext text-muted">
                            JPG, PNG · Max {{ maxSizeMb }}MB
                        </span>
                    </div>
                </div>

                <!-- Compact preview when a file is selected -->
                <div v-else class="sip-photo-preview-card">
                    <div class="sip-photo-preview-body">
                        <div class="sip-photo-preview-thumb-wrap">
                            <img :src="selectedFilePreview" alt="Selected Preview" class="sip-photo-preview-thumb" />
                        </div>

                        <div class="sip-photo-preview-meta">
                            <div class="sip-photo-preview-filename" :title="selectedFile.name">
                                {{ selectedFile.name }}
                            </div>
                            <div class="sip-photo-preview-filesize text-muted small">
                                {{ formatFileSize(selectedFile.size) }}
                            </div>

                            <div class="sip-photo-preview-actions mt-2">
                                <button type="button" class="btn btn-xs btn-outline-primary" @click="triggerFileInput" :disabled="isUploading">
                                    <i class="fi fi-rr-refresh me-1"></i> {{ trans('general.change_file') || 'Change' }}
                                </button>
                                <button type="button" class="btn btn-xs btn-outline-secondary" @click="clearSelectedFile" :disabled="isUploading">
                                    <i class="fi fi-rr-cross me-1"></i> {{ trans('general.clear') || 'Clear' }}
                                </button>
                                <input
                                    type="file"
                                    ref="fileInput"
                                    class="sip-photo-hidden-input"
                                    accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                                    @change="onFileChange"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Subtle remove link (no current-photo card) -->
                <div v-if="hasCustomPhoto && !selectedFile" class="sip-photo-remove-link-wrap">
                    <button
                        type="button"
                        class="sip-photo-remove-link"
                        :disabled="isUploading || isRemoving"
                        v-confirm="{ok: confirmRemovePhoto()}"
                    >
                        <span v-if="isRemoving" class="spinner-border spinner-border-sm me-1" role="status"></span>
                        <span>{{ isRemoving ? (trans('general.processing') || 'Removing...') : (trans('student.remove_photo') || 'Remove current photo') }}</span>
                    </button>
                </div>
            </div>

            <div class="sip-drawer-form-footer modal-footer w-100">
                <button
                    type="button"
                    class="btn btn-outline-light sip-drawer-btn-cancel"
                    @click="closeModal"
                    :disabled="isUploading || isRemoving"
                >
                    <em class="icon ni ni-cross"></em>
                    <span>{{ trans('general.cancel') || 'Cancel' }}</span>
                </button>

                <button
                    type="button"
                    class="btn btn-primary sip-drawer-btn-save"
                    :disabled="!selectedFile || isUploading || isRemoving"
                    @click="uploadPhoto"
                >
                    <span v-if="isUploading" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                    <em v-else class="icon ni ni-upload-cloud me-1"></em>
                    <span>{{ isUploading ? (trans('general.uploading') || 'Uploading...') : (trans('student.upload_and_save') || 'Upload & Save') }}</span>
                </button>
            </div>
        </div>
    </b-modal>
</template>

<script>
    export default {
        name: 'profile-photo-drawer',
        props: {
            id: {
                type: String,
                default: 'modal-profile-photo'
            },
            title: {
                type: String,
                default: ''
            },
            uploadPath: {
                type: String,
                required: true
            },
            removePath: {
                type: String,
                required: true
            },
            currentPhoto: {
                type: String,
                default: ''
            },
            name: {
                type: String,
                default: ''
            },
            code: {
                type: [String, Number],
                default: ''
            },
            codeLabel: {
                type: String,
                default: 'Adm #'
            },
            subBadge: {
                type: String,
                default: ''
            },
            subBadgeIcon: {
                type: String,
                default: 'fi fi-rr-graduation-cap'
            },
            status: {
                type: Object,
                default: () => ({ label: 'Active', type: 'success', icon: 'fi fi-rr-check-circle' })
            },
            gender: {
                type: String,
                default: 'male'
            },
            type: {
                type: String,
                default: 'student' // 'student' or 'employee'
            },
            maxSizeMb: {
                type: Number,
                default: 2
            }
        },
        data() {
            return {
                selectedFile: null,
                selectedFilePreview: '',
                isDragOver: false,
                isUploading: false,
                isRemoving: false
            };
        },
        computed: {
            modalTitle() {
                if (this.title) return this.title;
                if (this.type === 'employee') {
                    return this.trans('employee.change_photo') || 'Update Employee Photo';
                }
                return this.trans('student.change_photo') || 'Update Student Photo';
            },
            hasCustomPhoto() {
                return !!(this.currentPhoto && !this.currentPhoto.includes('avatar_'));
            }
        },
        methods: {
            onModalShow() {
                this.selectedFile = null;
                this.selectedFilePreview = '';
                this.isDragOver = false;
                this.isUploading = false;
                this.isRemoving = false;
            },
            onModalHidden() {
                this.clearSelectedFile();
            },
            triggerFileInput() {
                if (this.$refs.fileInput) {
                    this.$refs.fileInput.click();
                }
            },
            onDragEnter() {
                this.isDragOver = true;
            },
            onDragLeave() {
                this.isDragOver = false;
            },
            onDrop(e) {
                this.isDragOver = false;
                const files = (e.dataTransfer && e.dataTransfer.files) || [];
                if (files.length) {
                    this.handleFile(files[0]);
                }
            },
            onFileChange(e) {
                const files = (e.target && e.target.files) || [];
                if (files.length) {
                    this.handleFile(files[0]);
                }
            },
            handleFile(file) {
                if (!file) return;

                const allowedExtensions = /(\.jpe?g|\.png|\.webp)$/i;
                if (!allowedExtensions.exec(file.name)) {
                    if (window.NioApp && typeof window.NioApp.Toast === 'function') {
                        window.NioApp.Toast(this.trans('general.file_not_supported') || 'Invalid file format. Please select JPG, PNG or WEBP.', 'error');
                    }
                    this.clearSelectedFile();
                    return;
                }

                const maxBytes = this.maxSizeMb * 1024 * 1024;
                if (file.size > maxBytes) {
                    if (window.NioApp && typeof window.NioApp.Toast === 'function') {
                        window.NioApp.Toast((this.trans('general.file_too_large') || 'File is too large.') + ` Maximum size allowed is ${this.maxSizeMb}MB.`, 'error');
                    }
                    this.clearSelectedFile();
                    return;
                }

                this.selectedFile = file;
                const reader = new FileReader();
                reader.onload = (e) => {
                    this.selectedFilePreview = e.target.result;
                };
                reader.readAsDataURL(file);
            },
            clearSelectedFile() {
                this.selectedFile = null;
                this.selectedFilePreview = '';
                if (this.$refs.fileInput) {
                    this.$refs.fileInput.value = '';
                }
            },
            formatFileSize(bytes) {
                const n = Number(bytes) || 0;
                if (!n) return '';
                if (n < 1024) return n + ' B';
                if (n < 1048576) return (n / 1024).toFixed(1) + ' KB';
                return (n / 1048576).toFixed(2) + ' MB';
            },
            uploadPhoto() {
                if (!this.selectedFile || this.isUploading) return;

                this.isUploading = true;
                let formData = new FormData();
                formData.append('image', this.selectedFile);

                axios.post('/api' + this.uploadPath, formData)
                    .then(response => {
                        if (window.NioApp && typeof window.NioApp.Toast === 'function') {
                            window.NioApp.Toast(response.message || 'Photo uploaded successfully.', 'success');
                        }
                        this.$emit('uploaded', response.image || response.data);
                        this.clearSelectedFile();
                        this.closeModal();
                    })
                    .catch(error => {
                        if (error && error.response && error.response.data && error.response.data.message) {
                            if (window.NioApp && typeof window.NioApp.Toast === 'function') {
                                window.NioApp.Toast(error.response.data.message, 'error');
                            }
                        } else if (error && error.response && error.response.status === 422) {
                            if (window.NioApp && typeof window.NioApp.Toast === 'function') {
                                window.NioApp.Toast(error.response.error || error.response.data.error || 'Validation error', 'error');
                            }
                        } else if (window.helper && typeof window.helper.showErrorMsg === 'function') {
                            window.helper.showErrorMsg(error);
                        }
                    })
                    .finally(() => {
                        this.isUploading = false;
                    });
            },
            confirmRemovePhoto() {
                return dialog => this.executeRemovePhoto();
            },
            executeRemovePhoto() {
                this.isRemoving = true;
                axios.delete('/api' + this.removePath)
                    .then(response => {
                        if (window.NioApp && typeof window.NioApp.Toast === 'function') {
                            window.NioApp.Toast(response.message || 'Photo removed successfully.', 'success');
                        }
                        this.$emit('removed', '');
                        this.closeModal();
                    })
                    .catch(error => {
                        if (error && error.response && error.response.data && error.response.data.message) {
                            if (window.NioApp && typeof window.NioApp.Toast === 'function') {
                                window.NioApp.Toast(error.response.data.message, 'error');
                            }
                        } else if (window.helper && typeof window.helper.showErrorMsg === 'function') {
                            window.helper.showErrorMsg(error);
                        }
                    })
                    .finally(() => {
                        this.isRemoving = false;
                    });
            },
            closeModal() {
                this.clearSelectedFile();
                if (this.$bvModal) {
                    this.$bvModal.hide(this.id);
                }
                this.$emit('close');
            }
        }
    }
</script>
